import type { PlaybookTestCustomer } from "@repo/app-types/entities/playbookTestCase";
import {
  allPlaybookTestFactDisclosures,
  playbookTestFactDisclosure,
} from "@repo/app-types/entities/playbookTestCase";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { KeyValuePairList } from "@repo/ui/FormLists/keyValuePairList";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import {
  IconChevronDown,
  IconPlus,
  IconTrash,
  IconWaveSine,
} from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";

type PlaybookTestFact = PlaybookTestCustomer["facts"][number];
type PlaybookTestSpeechStyle = NonNullable<
  PlaybookTestCustomer["speech_style"]
>;

const factDisclosureLabels: Record<PlaybookTestFact["disclosure"], string> = {
  [playbookTestFactDisclosure.Volunteered]: "Volunteered",
  [playbookTestFactDisclosure.OnRequest]: "When asked",
  [playbookTestFactDisclosure.Reluctant]: "Reluctantly",
  [playbookTestFactDisclosure.Corrected]: "Corrects the rep",
  [playbookTestFactDisclosure.Refused]: "Refuses",
};

const factDisclosureOptions = allPlaybookTestFactDisclosures.map(
  (disclosure) => ({
    value: disclosure,
    label: factDisclosureLabels[disclosure],
  }),
);

/** An empty brief, so switching a case to detailed starts from a known shape. */
export const createEmptyPlaybookTestCustomer = (): PlaybookTestCustomer => ({
  goal: null,
  temperament: "",
  decision_style: "",
  situations: [],
  arc: "",
  facts: [],
  objections: [],
  conversion_conditions: [],
  speech_style: null,
});

/** Empty means "no constraint", which the brief stores as null rather than "". */
const toNullableText = (value: string): string | null =>
  value.trim() === "" ? null : value;

interface EditableListProps {
  label: string;
  description: string;
  addButtonLabel: string;
  onAdd: () => void;
  children: ReactNode;
}

/**
 * The shell the brief's lists share: a labeled section, its rows, and one add
 * button. Rows differ enough between facts and conditions that only the frame
 * is worth sharing.
 */
const EditableList: FC<EditableListProps> = (props) => {
  const { label, description, addButtonLabel, onAdd, children } = props;

  return (
    <div className="flex flex-col space-y-2">
      <Label size={Size.SMALL}>{label}</Label>
      <FieldDescription>{description}</FieldDescription>
      {children}
      <div>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.MEDIUM}
          icon={<IconPlus className="h-4 w-4" />}
          onClick={onAdd}
        >
          {addButtonLabel}
        </Button>
      </div>
    </div>
  );
};

interface SpeechStyleDisclosureProps {
  speechStyle: PlaybookTestSpeechStyle;
}

const turnShapeLabels: Record<
  keyof NonNullable<PlaybookTestSpeechStyle["turn_shape"]>,
  string
> = {
  median_words: "Median words",
  p25_words: "25th percentile words",
  p75_words: "75th percentile words",
  p90_words: "90th percentile words",
  short_share: "Short turns",
  long_share: "Long turns",
  spoken_marker_share: "Turns with a spoken marker",
  multi_question_share: "Turns with several questions",
};

/** The shares are fractions of the donor's customer turns, not counts. */
const isShareMetric = (metric: string): boolean => metric.endsWith("_share");

const formatTurnShapeValue = (metric: string, value: number): string =>
  isShareMetric(metric) ? `${Math.round(value * 100)}%` : `${value}`;

const SpeechStyleDisclosure: FC<SpeechStyleDisclosureProps> = (props) => {
  const { speechStyle } = props;
  const turnShape = speechStyle.turn_shape;

  return (
    <Disclosure className="rounded-lg border border-border-light bg-white">
      <Heading>
        <AriaButton
          slot="trigger"
          className="group w-full min-w-0 cursor-pointer text-left"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <IconWaveSine className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
              Speech style
            </div>
            <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
          </div>
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="space-y-4 border-t border-border-light px-3 py-3">
          <FieldDescription>
            Derived from real calls, so the simulated customer talks the way
            this kind of customer talks. Not editable.
          </FieldDescription>
          {turnShape ? (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {Object.entries(turnShape).map(([metric, value]) => (
                <div key={metric} className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">
                    {turnShapeLabels[metric as keyof typeof turnShapeLabels] ??
                      metric}
                  </dt>
                  <dd className="text-foreground tabular-nums">
                    {formatTurnShapeValue(metric, value)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {speechStyle.style_manual.length > 0 ? (
            <div className="space-y-2">
              <Label size={Size.SMALL}>Style manual</Label>
              <ul className="space-y-2">
                {speechStyle.style_manual.map((entry, index) => (
                  <li key={index} className="text-sm">
                    <div className="text-foreground">{entry.command}</div>
                    <div className="text-muted-foreground italic">
                      &ldquo;{entry.example}&rdquo;
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {speechStyle.exchanges.length > 0 ? (
            <div className="space-y-2">
              <Label size={Size.SMALL}>Sampled exchanges</Label>
              <ul className="space-y-2">
                {speechStyle.exchanges.map((exchange, index) => (
                  <li key={index} className="space-y-0.5 text-sm">
                    {exchange.rep === null ? null : (
                      <div className="text-muted-foreground">
                        Rep: {exchange.rep}
                      </div>
                    )}
                    <div className="text-foreground">
                      Customer: {exchange.customer}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export interface PlaybookTestCustomerEditorProps {
  customer: PlaybookTestCustomer;
  onChange: (customer: PlaybookTestCustomer) => void;
}

export const PlaybookTestCustomerEditor: FC<PlaybookTestCustomerEditorProps> = (
  props,
) => {
  const { customer, onChange } = props;

  const updateFact = (index: number, changes: Partial<PlaybookTestFact>) => {
    onChange({
      ...customer,
      facts: customer.facts.map((fact, factIndex) =>
        factIndex === index ? { ...fact, ...changes } : fact,
      ),
    });
  };

  return (
    <div className="space-y-4">
      <Input
        label="Goal"
        value={customer.goal ?? ""}
        onChange={(event) =>
          onChange({ ...customer, goal: toNullableText(event.target.value) })
        }
        placeholder="Get a quote they can compare before the weekend"
        description="What they are trying to get out of the call. Leave empty when they have no goal."
      />
      <Input
        label="Temperament"
        value={customer.temperament}
        onChange={(event) =>
          onChange({ ...customer, temperament: event.target.value })
        }
        placeholder="Guarded, warms up once the numbers are concrete"
      />
      <Input
        label="Decision style"
        value={customer.decision_style}
        onChange={(event) =>
          onChange({ ...customer, decision_style: event.target.value })
        }
        placeholder="Wants to check with a partner before committing"
      />
      <Textarea
        label="Arc"
        value={customer.arc}
        onChange={(value) => onChange({ ...customer, arc: value })}
        placeholder="Opens with a price question, softens once the timeline is clear, asks to be called back."
        description="How this kind of call tends to run, in a sentence or two."
        height="5rem"
      />

      <KeyValuePairList
        label="Situations"
        description="The circumstances this customer is calling in. Each one is a short name and a sentence of detail."
        addButtonLabel="Add situation"
        keyPlaceholder="Shopping around after a rate hike"
        valuePlaceholder="Their renewal came in $40 a month higher and they want to compare before it starts."
        value={customer.situations.map((situation) => ({
          key: situation.label,
          value: situation.description,
        }))}
        onChange={(pairs) =>
          onChange({
            ...customer,
            situations: pairs.map((pair) => ({
              label: pair.key,
              description: pair.value,
            })),
          })
        }
      />

      <EditableList
        label="Facts"
        description="Something the customer knows and the concrete answer they give when it comes up. Every run plays the same answer."
        addButtonLabel="Add fact"
        onAdd={() =>
          onChange({
            ...customer,
            facts: [
              ...customer.facts,
              {
                fact: "",
                value: "",
                disclosure: playbookTestFactDisclosure.OnRequest,
                phase: null,
              },
            ],
          })
        }
      >
        {customer.facts.map((fact, index) => (
          <div
            key={index}
            className="space-y-2 rounded-lg border border-border-light p-3"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  accessibleLabel="Fact"
                  placeholder="Current auto premium"
                  value={fact.fact}
                  onChange={(event) =>
                    updateFact(index, { fact: event.target.value })
                  }
                />
              </div>
              <IconButton
                icon={<IconTrash className="h-4 w-4" />}
                variant={ButtonVariant.Tertiary}
                onClick={() =>
                  onChange({
                    ...customer,
                    facts: customer.facts.filter(
                      (_, factIndex) => factIndex !== index,
                    ),
                  })
                }
                accessibleLabel={`Remove ${fact.fact || "fact"}`}
              />
            </div>
            <Input
              accessibleLabel="Value"
              placeholder="$142 a month, up from $118 last year"
              value={fact.value}
              onChange={(event) =>
                updateFact(index, { value: event.target.value })
              }
            />
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <SimpleSelect
                  label="Disclosure"
                  isLabelHidden
                  isFullWidth
                  options={factDisclosureOptions}
                  value={fact.disclosure}
                  onChange={(disclosure) => updateFact(index, { disclosure })}
                />
              </div>
              <div className="min-w-0 flex-1">
                <Input
                  accessibleLabel="Phase"
                  placeholder="Phase, e.g. discovery (optional)"
                  value={fact.phase ?? ""}
                  onChange={(event) =>
                    updateFact(index, {
                      phase: toNullableText(event.target.value),
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </EditableList>

      <KeyValuePairList
        label="Objections"
        description="What they push back on, and the phase they first raise it in. Leave the phase empty and they can raise it at any point."
        addButtonLabel="Add objection"
        keyPlaceholder="I already have a policy through work"
        valuePlaceholder="Phase, e.g. handle objections (optional)"
        value={customer.objections.map((objection) => ({
          key: objection.objection,
          value: objection.phase ?? "",
        }))}
        onChange={(pairs) =>
          onChange({
            ...customer,
            objections: pairs.map((pair) => ({
              objection: pair.key,
              phase: toNullableText(pair.value),
            })),
          })
        }
      />

      <EditableList
        label="Conversion conditions"
        description="What has to be true before this customer says yes."
        addButtonLabel="Add condition"
        onAdd={() =>
          onChange({
            ...customer,
            conversion_conditions: [...customer.conversion_conditions, ""],
          })
        }
      >
        {customer.conversion_conditions.map((condition, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                accessibleLabel="Conversion condition"
                placeholder="They hear a price before the call ends"
                value={condition}
                onChange={(event) =>
                  onChange({
                    ...customer,
                    conversion_conditions: customer.conversion_conditions.map(
                      (existing, conditionIndex) =>
                        conditionIndex === index
                          ? event.target.value
                          : existing,
                    ),
                  })
                }
              />
            </div>
            <IconButton
              icon={<IconTrash className="h-4 w-4" />}
              variant={ButtonVariant.Tertiary}
              onClick={() =>
                onChange({
                  ...customer,
                  conversion_conditions: customer.conversion_conditions.filter(
                    (_, conditionIndex) => conditionIndex !== index,
                  ),
                })
              }
              accessibleLabel={`Remove ${condition || "condition"}`}
            />
          </div>
        ))}
      </EditableList>

      {customer.speech_style ? (
        <SpeechStyleDisclosure speechStyle={customer.speech_style} />
      ) : null}
    </div>
  );
};
